import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Directive,
  HostBinding,
  Input,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent, ButtonDirective } from '@zelis/dls/button';
import type { CardEmphasis } from '@zelis/dls/card';
import { ThemeLozengeComponent } from '@zelis/dls/lozenge';

export type CalloutCardVariant = 'outlined' | 'plain' | 'elevated';

export const LARGE_SMALL_BREAKPOINT = 768;

@Component({
  selector: 'zelis-callout-card-content',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `<ng-content></ng-content>`,
})
export class CalloutCardContentComponent {}

@Directive({
  selector: 'img[zelisCalloutCardImage]',
})
export class CalloutCardImageDirective {}

/**
 * The callout-card component uses content projection to be able to selectively render the provided elements, and apply any necessary constraints based on the design system.
 *
 * For the **main body**, it expects to be given a `<zelis-callout-card-content>` component, for example:
 *
 * ```html
 * <zelis-callout-card variant="plain" title="Title">
 *  <!-- ... more elements here -->
 *  <zelis-callout-card-content>
 *    This is the main body of the callout card.
 *  </zelis-callout-card-content>
 *  <!-- ... more elements here -->
 * </zelis-callout-card>
 * ```
 *
 * For the **top-left**, it can accept either an instance of `<mat-icon>` or an `<img>` tag annotated with the `CalloutCardImageDirective`, for example:
 *
 * ```html
 * <zelis-callout-card variant="plain" title="Title">
 *  <!-- ... more elements here -->
 *  <img zelisCalloutCardImage />
 *  <!-- ... more elements here -->
 * </zelis-callout-card>
 * ```
 *
 * OR
 *
 * ```html
 * <zelis-callout-card variant="plain" title="Title">
 *  <!-- ... more elements here -->
 *  <mat-icon svgIcon="dls-globe-light"></mat-icon>
 *  <!-- ... more elements here -->
 * </zelis-callout-card>
 * ```
 *
 * Optionally, for the **top-right**, it can accept either an instance of `<zelis-theme-lozenge>`, or a `<button>` or `<a>` tag annotated with the `ButtonDirective`, or `<zelis-button>`, for example:
 *
 * ```html
 * <zelis-callout-card variant="plain" title="Title">
 *  <!-- ... more elements here -->
 *  <zelis-button
 *    variant="flat"
 *    color="primary"
 *    type="button"
 *    (click)="buttonClicked($event)"
 *  >Button</zelis-button>
 *  <!-- ... more elements here -->
 * </zelis-callout-card>
 * ```
 *
 * OR
 *
 * ```html
 * <zelis-callout-card variant="plain" title="Title">
 *  <!-- ... more elements here -->
 *  <zelis-theme-lozenge emphasis="high" size="medium" color="system-green">
 *    Earn Up to $250
 *  </zelis-theme-lozenge>
 *  <!-- ... more elements here -->
 * </zelis-callout-card>
 * ```
 */
@Component({
  selector: 'zelis-callout-card',
  templateUrl: './callout-card.component.html',
  styleUrls: ['./callout-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalloutCardComponent implements AfterContentInit {
  @HostBinding() class = 'zelis-dls';

  @ContentChild(ButtonDirective) button: ButtonDirective | undefined;
  @ContentChild(ButtonComponent) zelisButton: ButtonComponent | undefined;
  @ContentChild(MatIcon) icon: MatIcon | undefined;
  @ContentChild(CalloutCardImageDirective) image:
    | CalloutCardImageDirective
    | undefined;
  @ContentChild(ThemeLozengeComponent) lozenge:
    | ThemeLozengeComponent
    | undefined;

  /**
   * Optional visual emphasis controls for the card.
   *
   * The emphasis position is always 'left' for the callout card so we do not expose
   * the position property to the consumer.
   */
  @Input() emphasisColor: CardEmphasis['color'] | undefined;
  @Input() variant!: CalloutCardVariant;
  @Input() title = '';
  @Input() subtitle = '';

  /**
   * For this component we need to go beyond tailwind's viewport based breakpoints
   * and alter the layout based on the size of the container.
   *
   * Based on the design spec we have two different sizes to consider:
   * - "small" - for a container width of less than or equal to ${LARGE_SMALL_BREAKPOINT}px
   * - "large" - for a container width of greater than ${LARGE_SMALL_BREAKPOINT}px
   */
  inferredSize: 'small' | 'large' = 'large';
  private readonly inferredSizeBreakpoint = LARGE_SMALL_BREAKPOINT;

  ngAfterContentInit(): void {
    if ((this.button || this.zelisButton) && this.lozenge) {
      throw new Error(
        'You cannot use both a button and a lozenge within the same callout-card'
      );
    }
    if (this.icon && this.image) {
      throw new Error(
        'You cannot use both an icon and an image within the same callout-card'
      );
    }
  }

  onResize(entries: ResizeObserverEntry[]): void {
    if (!entries || !entries.length) {
      return;
    }
    this.inferredSize =
      entries[0].contentRect.width <= this.inferredSizeBreakpoint
        ? 'small'
        : 'large';

    switch (this.inferredSize) {
      case 'small':
        this.setButtonFullWidth(true);
        break;
      case 'large':
        this.setButtonFullWidth(false);
        break;
    }
  }

  private setButtonFullWidth(fullWidth: boolean) {
    if (this.button) {
      this.button.fullWidth = fullWidth;
    }
    if (this.zelisButton) {
      this.zelisButton.fullWidth = fullWidth;
      this.zelisButton.ngOnChanges();
    }
  }
}
